/**
 * Cyber Essentials 14-day Breaches — evidence-ready table.
 *
 * Two views over the same data, switched by the "Group by" toggle:
 *
 *  • By version (default) — one row per vulnerable app version whose highest
 *    severity is CRITICAL/HIGH and whose fix has been available for > 14 days.
 *    This is the view a Cyber Essentials assessor would ask for.
 *  • By product — one row per product, rolled up worst-case, with the
 *    versions expandable underneath. A product with a hundred builds in the
 *    estate is unreadable one row at a time; this is the view you skim to
 *    see WHICH products are in breach and what single version clears them.
 *
 * Both are sorted KEV → breach → severity → days overdue → device count so
 * the worst items are always at the top.
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  RefreshCw, ShieldAlert, Flame, Download, Info, ChevronRight, ChevronDown,
} from "lucide-react";
import { PosturePill } from "./RemediationPostureWidget";

const SEVERITY_COLOR = {
  CRITICAL: "bg-red-600 text-white",
  HIGH: "bg-orange-600 text-white",
};

const CARD_BG =
  "bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] " +
  "border border-[rgba(229,239,250,0.1)] rounded-xl";

const KpiTile = ({ label, value, sub, tone = "default", testId }) => {
  const toneClasses = {
    default: "text-white",
    danger:  "text-red-300",
    warning: "text-orange-300",
  }[tone] || "text-white";
  return (
    <div className={`${CARD_BG} p-4`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-white/60">
        {label}
      </div>
      <div className={`text-2xl font-semibold leading-tight ${toneClasses}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
    </div>
  );
};

const OverdueBadge = ({ days, isBreach, hasFix }) => {
  const d = days || 0;
  // Within the 14-day window (or no fix yet) -> not a breach: neutral styling.
  if (!isBreach) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold bg-white/10 text-white/70"
        title={
          hasFix === false
            ? `Exposed ${d} day(s) — no fixed version published yet`
            : `Exposed ${d} day(s) — still inside the 14-day remediation window`
        }
        data-testid="overdue-badge"
      >
        {d}d {hasFix === false ? "· no fix" : "· in window"}
      </span>
    );
  }
  const tone =
    d >= 60 ? "bg-red-800 text-white" :
    d >= 30 ? "bg-red-600 text-white" :
    d >= 15 ? "bg-orange-600 text-white" :
              "bg-amber-600 text-white";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold ${tone}`}
      title={`${d} days since first exposed — beyond the Cyber Essentials 14-day window`}
      data-testid="overdue-badge"
    >
      {d}d overdue
    </span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return null;
  }
};

// Sub-line under the overdue badge. The overdue clock is measured purely from
// when ALICE first saw the vulnerable install in the estate — never the CVE
// publish date — so we surface that date. The CVE publish date is shown only
// as muted context.
const OverdueContext = ({ row }) => {
  const detected = fmtDate(row.first_detected_at);
  const published = fmtDate(row.worst_cve_published);
  if (!detected && !published) return null;
  return (
    <div className="mt-1 space-y-0.5" data-testid="overdue-context">
      {detected && (
        <div
          className="text-[10px] text-white/60"
          title={`Overdue is counted from when ALICE first saw this version in your estate (${detected}), not the CVE age.`}
          data-testid="overdue-first-seen-note"
        >
          First seen in estate: {detected}
        </div>
      )}
      {published && (
        <div className="text-[10px] text-white/35" data-testid="overdue-published-note">
          CVE published {published}
        </div>
      )}
    </div>
  );
};

const CEBreachesTab = () => {
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [severityFilter, setSeverityFilter] = useState("all");
  const [postureFilter, setPostureFilter] = useState("all");
  // Product by default — one row per product with versions expandable
  // underneath; the per-version evidence list is a toggle away.
  const [groupBy, setGroupBy] = useState("product");
  // family_name -> expanded, for the per-product version drill-down.
  const [expanded, setExpanded] = useState({});
  const byProduct = groupBy === "product";

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    return { Authorization: `Bearer ${token}` };
  }, [getAccessToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const params = { page_size: 500, group_by: groupBy };
      if (severityFilter !== "all") params.severity = severityFilter;
      if (postureFilter !== "all") params.posture = postureFilter;
      const r = await axios.get(`${API}/cve/ce-breaches`, { headers, params });
      setRows(r.data?.items || []);
      setSummary(r.data?.summary || {});
    } catch (err) {
      console.warn("CE breaches load failed", err);
      toast.error("Failed to load Cyber Essentials breaches");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, severityFilter, postureFilter, groupBy]);

  useEffect(() => { load(); }, [load]);

  // Collapse any open drill-downs when the view changes, so switching back to
  // "By product" doesn't restore rows the new filters may have removed.
  useEffect(() => { setExpanded({}); }, [groupBy, severityFilter, postureFilter]);

  const toggleRow = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const exportCsv = () => {
    if (rows.length === 0) {
      toast.info("Nothing to export");
      return;
    }
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    // The product export keeps the vulnerable versions in a single cell so the
    // consolidated CSV still names the exact builds behind every row.
    const header = byProduct
      ? [
        "Product", "Vulnerable versions", "Version count", "Devices",
        "Installs", "Severity", "KEV", "CVEs", "Worst days overdue",
        "First seen in estate", "Recommended fix", "ALICE posture",
      ]
      : [
        "Application", "Version", "Devices", "Severity", "KEV",
        "CVEs", "Days overdue", "First seen in estate", "CVE published",
        "Recommended fix", "ALICE posture",
      ];
    const lines = [header.join(",")];
    rows.forEach((r) => {
      lines.push((byProduct
        ? [
          escape(r.app_name || r.family_name),
          escape((r.versions || []).map((v) => v.version).join(" | ")),
          escape(r.version_count),
          escape(r.device_count),
          escape(r.install_count),
          escape(r.highest_severity),
          escape(r.has_kev ? "Yes" : ""),
          escape(r.cve_count),
          escape(r.days_overdue),
          escape(fmtDate(r.first_detected_at) || ""),
          escape(r.worst_fix_version),
          escape(r.posture),
        ]
        : [
          escape(r.app_name),
          escape(r.version),
          escape(r.device_count),
          escape(r.highest_severity),
          escape(r.has_kev ? "Yes" : ""),
          escape(r.cve_count),
          escape(r.days_overdue),
          escape(fmtDate(r.first_detected_at) || ""),
          escape(fmtDate(r.worst_cve_published) || ""),
          escape(r.worst_fix_version),
          escape(r.posture),
        ]
      ).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `alice_ce_breaches_by_${byProduct ? "product" : "version"}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="ce-breaches-tab">
      {/* KPI band */}
      <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          label="Total breach installs"
          value={(summary.total_installs || 0).toLocaleString()}
          sub={
            byProduct
              ? `${summary.products_affected || 0} products affected`
              : `${summary.app_versions_affected || 0} app versions affected`
          }
          tone={summary.total_installs > 0 ? "danger" : "default"}
          testId="kpi-ce-total"
        />
        <KpiTile
          label="Critical installs"
          value={(summary.critical_installs || 0).toLocaleString()}
          sub="Severity: Critical, > 14 days"
          tone={summary.critical_installs > 0 ? "danger" : "default"}
          testId="kpi-ce-critical"
        />
        <KpiTile
          label="High installs"
          value={(summary.high_installs || 0).toLocaleString()}
          sub="Severity: High, > 14 days"
          tone={summary.high_installs > 0 ? "warning" : "default"}
          testId="kpi-ce-high"
        />
        <KpiTile
          label="Worst overdue"
          value={
            summary.worst_days_overdue
              ? `${summary.worst_days_overdue}d`
              : "—"
          }
          sub="Longest breach against the 14-day rule"
          tone={
            summary.worst_days_overdue >= 30 ? "danger"
              : summary.worst_days_overdue > 0 ? "warning"
              : "default"
          }
          testId="kpi-ce-worst"
        />
      </div>

      {/* Toolbar */}
      <div className={`${CARD_BG} p-3 flex flex-wrap items-center gap-2 justify-between`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-white/60 uppercase tracking-wide">
            Severity
          </span>
          {["all", "CRITICAL", "HIGH"].map((s) => (
            <Button
              key={s}
              variant={severityFilter === s ? "default" : "outline"}
              onClick={() => setSeverityFilter(s)}
              size="sm"
              className={
                severityFilter === s
                  ? "bg-cyan-600 hover:bg-cyan-500 text-white"
                  : "bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10"
              }
              data-testid={`ce-severity-filter-${s}`}
            >
              {s === "all" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}
            </Button>
          ))}
          <span className="text-xs text-white/60 uppercase tracking-wide ml-3">
            Posture
          </span>
          {[
            ["all", "All"],
            ["unmanaged", "Unmanaged"],
            ["stale_package", "Stale"],
            ["auto_resolving", "Auto-resolving"],
          ].map(([val, label]) => (
            <Button
              key={val}
              variant={postureFilter === val ? "default" : "outline"}
              onClick={() => setPostureFilter(val)}
              size="sm"
              className={
                postureFilter === val
                  ? "bg-cyan-600 hover:bg-cyan-500 text-white"
                  : "bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10"
              }
              data-testid={`ce-posture-filter-${val}`}
            >
              {label}
            </Button>
          ))}
          <span className="text-xs text-white/60 uppercase tracking-wide ml-3">
            Group by
          </span>
          {[
            ["version", "Version"],
            ["product", "Product"],
          ].map(([val, label]) => (
            <Button
              key={val}
              variant={groupBy === val ? "default" : "outline"}
              onClick={() => setGroupBy(val)}
              size="sm"
              title={
                val === "product"
                  ? "One row per product — every vulnerable version rolled up worst-case"
                  : "One row per app version — the full evidence list"
              }
              className={
                groupBy === val
                  ? "bg-cyan-600 hover:bg-cyan-500 text-white"
                  : "bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10"
              }
              data-testid={`ce-group-by-${val}`}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10"
            data-testid="ce-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            className="bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10"
            data-testid="ce-export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className={`${CARD_BG} overflow-hidden`}>
        <div className="max-h-[640px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b-[rgba(229,239,250,0.1)] hover:bg-transparent">
                <TableHead className="text-white font-semibold">
                  {byProduct ? "Product" : "Application"}
                </TableHead>
                <TableHead className="text-white font-semibold w-[150px]">
                  {byProduct ? "Vulnerable versions" : "Version"}
                </TableHead>
                <TableHead className="text-white font-semibold w-[90px] text-right">Devices</TableHead>
                <TableHead className="text-white font-semibold w-[110px]">Severity</TableHead>
                <TableHead className="text-white font-semibold w-[70px] text-right">CVEs</TableHead>
                <TableHead className="text-white font-semibold w-[130px]">
                  {byProduct ? "Worst overdue" : "Days overdue"}
                </TableHead>
                <TableHead className="text-white font-semibold w-[140px]">Recommended fix</TableHead>
                <TableHead className="text-white font-semibold w-[160px]">ALICE posture</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-6 text-white/70">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-white/60">
                    <ShieldAlert className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                    No vulnerable applications found for the current filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map((r, idx) => {
                const rowKey = byProduct
                  ? r.family_name || r.app_name
                  : `${r.family_name}-${r.version}-${idx}`;
                const isOpen = byProduct && !!expanded[rowKey];
                return (
                  <React.Fragment key={rowKey}>
                    <TableRow
                      className={`border-b-[rgba(229,239,250,0.06)] hover:bg-white/[0.04] ${byProduct ? "cursor-pointer" : ""}`}
                      onClick={byProduct ? () => toggleRow(rowKey) : undefined}
                      data-testid={`ce-breach-row-${idx}`}
                    >
                      <TableCell>
                        <div className="font-medium text-white flex items-center gap-2">
                          {byProduct && (
                            isOpen
                              ? <ChevronDown className="w-4 h-4 text-white/60 shrink-0" />
                              : <ChevronRight className="w-4 h-4 text-white/60 shrink-0" />
                          )}
                          {r.app_name}
                          {r.has_kev && (
                            <Badge
                              className="bg-rose-700 text-white text-[10px] inline-flex items-center gap-1"
                              title="Actively exploited (CISA KEV)"
                            >
                              <Flame className="w-3 h-3" /> KEV
                            </Badge>
                          )}
                        </div>
                        <div className="text-[11px] text-white/50">
                          {byProduct
                            ? `${r.breaching_version_count || 0} of ${r.version_count || 0} version(s) in breach`
                            : r.family_name}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-white">
                        {byProduct ? (
                          <span
                            className="inline-flex items-center rounded px-2 py-0.5 bg-white/10 text-white/80 font-sans text-[11px]"
                            data-testid={`ce-version-count-${idx}`}
                          >
                            {(r.version_count || 0).toLocaleString()} version{r.version_count === 1 ? "" : "s"}
                          </span>
                        ) : (r.version || "—")}
                      </TableCell>
                      <TableCell className="text-right text-white/90 font-medium">
                        {(r.device_count || 0).toLocaleString()}
                        {byProduct && r.install_count > r.device_count && (
                          <div
                            className="text-[10px] text-white/45 font-normal"
                            title="Some devices carry more than one vulnerable version; they are counted once above."
                          >
                            {r.install_count.toLocaleString()} installs
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${SEVERITY_COLOR[(r.highest_severity || "").toUpperCase()] || "bg-gray-600 text-white"} text-xs`}
                        >
                          {(r.highest_severity || "").toUpperCase() || "—"}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className="text-right text-white/85"
                        title={byProduct ? "Distinct CVEs across every vulnerable version" : undefined}
                      >
                        {r.cve_count || 0}
                      </TableCell>
                      <TableCell>
                        <OverdueBadge days={r.days_overdue || 0} isBreach={r.is_breach} hasFix={r.has_fix} />
                        <OverdueContext row={r} />
                      </TableCell>
                      <TableCell
                        className="font-mono text-xs text-emerald-300"
                        title={byProduct ? "Upgrading to this version clears every vulnerable version listed" : undefined}
                      >
                        {r.worst_fix_version || "—"}
                      </TableCell>
                      <TableCell>
                        <PosturePill posture={r.posture} testId={`ce-row-posture-${idx}`} />
                      </TableCell>
                    </TableRow>

                    {isOpen && (
                      <TableRow
                        className="border-b-[rgba(229,239,250,0.06)] hover:bg-transparent"
                        data-testid={`ce-breach-versions-${idx}`}
                      >
                        <TableCell colSpan={8} className="bg-black/20 p-0">
                          <div className="px-6 py-3">
                            <div className="text-[10px] uppercase tracking-wide text-white/50 mb-2">
                              Vulnerable versions of {r.app_name}
                            </div>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-white/50 text-left">
                                  <th className="font-medium py-1 pr-4">Version</th>
                                  <th className="font-medium py-1 pr-4 text-right">Devices</th>
                                  <th className="font-medium py-1 pr-4">Severity</th>
                                  <th className="font-medium py-1 pr-4 text-right">CVEs</th>
                                  <th className="font-medium py-1 pr-4">Days overdue</th>
                                  <th className="font-medium py-1 pr-4">Recommended fix</th>
                                  <th className="font-medium py-1">ALICE posture</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(r.versions || []).map((v, vIdx) => (
                                  <tr
                                    key={`${rowKey}-${v.version}-${vIdx}`}
                                    className="border-t border-white/[0.06]"
                                    data-testid={`ce-breach-version-${idx}-${vIdx}`}
                                  >
                                    <td className="py-1.5 pr-4 font-mono text-white">
                                      {v.version || "—"}
                                      {v.has_kev && (
                                        <Flame
                                          className="w-3 h-3 inline ml-1 text-rose-400"
                                          title="Actively exploited (CISA KEV)"
                                        />
                                      )}
                                    </td>
                                    <td className="py-1.5 pr-4 text-right text-white/85">
                                      {(v.device_count || 0).toLocaleString()}
                                    </td>
                                    <td className="py-1.5 pr-4">
                                      <Badge
                                        className={`${SEVERITY_COLOR[(v.highest_severity || "").toUpperCase()] || "bg-gray-600 text-white"} text-[10px]`}
                                      >
                                        {(v.highest_severity || "").toUpperCase() || "—"}
                                      </Badge>
                                    </td>
                                    <td className="py-1.5 pr-4 text-right text-white/75">
                                      {v.cve_count || 0}
                                    </td>
                                    <td className="py-1.5 pr-4">
                                      <OverdueBadge
                                        days={v.days_overdue || 0}
                                        isBreach={v.is_breach}
                                        hasFix={v.ce_status !== "no_fix_available"}
                                      />
                                    </td>
                                    <td className="py-1.5 pr-4 font-mono text-emerald-300">
                                      {v.worst_fix_version || "—"}
                                    </td>
                                    <td className="py-1.5">
                                      <PosturePill
                                        posture={v.posture}
                                        testId={`ce-version-posture-${idx}-${vIdx}`}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default CEBreachesTab;
